import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SellerForm from '../components/SellerForm';
import EditSellerForm from '../components/EditSellerForm';
import '../styles/SellerForm.css';
import '../styles/Sellers.css';

const Sellers = () => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [refreshData, setRefreshData] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editingSeller, setEditingSeller] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          navigate('/login');
          return;
        }
        
        // Fetch sellers and subscription data in parallel
        const [sellersResponse, subscriptionResponse] = await Promise.all([
          axios.get('/api/sellers/sellers', {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }),
          axios.get('/api/subscriptions/me', {
            headers: {
              Authorization: `Bearer ${token}`
            }
          })
        ]);
        
        setSellers(sellersResponse.data);
        setSubscription(subscriptionResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('No se pudieron cargar los datos. Por favor, inténtelo de nuevo.');
        
        // If unauthorized, redirect to login
        if (error.response && error.response.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, refreshData]);

  const handleSellerAdded = () => {
    // Refresh the sellers list
    setRefreshData(!refreshData);
    // Hide the form
    setShowForm(false);
    // Show success message
    setSuccessMessage('Vendedor agregado exitosamente');
    
    // Hide message after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const handleEditClick = (seller) => {
    setEditingSeller(seller);
    setShowEditForm(true);
    setShowForm(false); // Cerrar el formulario de agregar si está abierto
  };

  const handleSellerUpdated = () => {
    // Refresh the sellers list
    setRefreshData(!refreshData);
    // Hide the edit form
    setShowEditForm(false);
    setEditingSeller(null);
    // Show success message
    setSuccessMessage('Vendedor actualizado exitosamente');
    
    // Hide message after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const handleToggleStatus = async (sellerId, currentStatus) => {
    try {
      setIsUpdating(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login');
        return;
      }
      
      // Actualizar el estado del vendedor (invertir el estado actual)
      await axios.put(`/api/sellers/seller/${sellerId}/status`, 
        { alive: !currentStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // Actualizar la lista de vendedores
      setRefreshData(!refreshData);
      
    } catch (error) {
      console.error('Error al actualizar el estado del vendedor:', error);
      setError('Error al actualizar el estado del vendedor. Por favor, inténtelo de nuevo.');
      
      // Si no está autorizado, redirigir al login
      if (error.response && error.response.status === 401) {
        navigate('/login');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  // Check if the "Add User" button should be disabled
  const isAddUserButtonDisabled = () => {
    // If subscription data is not loaded yet, disable the button
    if (!subscription) {
      return true;
    }

    // Check if subscription type is 'trial' or 'personal'
    const isTrialOrPersonal = subscription.type_subscript === 'trial' || subscription.type_subscript === 'personal';
    
    // If it's trial or personal, check if there are already 2 or more users
    if (isTrialOrPersonal) {
      return sellers.length >= 2;
    }

    // For other subscription types, don't disable the button
    return false;
  };

  // Get tooltip message for the "Add User" button
  const getAddUserButtonTooltip = () => {
    if (!subscription) {
      return 'Cargando información de suscripción...';
    }

    const isTrialOrPersonal = subscription.type_subscript === 'trial' || subscription.type_subscript === 'personal';
    
    if (isTrialOrPersonal && sellers.length >= 2) {
      return `Las suscripciones ${subscription.type_subscript} solo permiten un máximo de 2 usuarios. Actualiza tu suscripción para agregar más usuarios.`;
    }

    return 'Agregar nuevo usuario al negocio';
  };

  if (loading && sellers.length === 0) {
    return <div className="loading">Cargando vendedores...</div>;
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <h1>Gestión de Vendedores</h1>
        <button onClick={() => navigate('/dashboard')} className="back-btn">
          Volver al Dashboard
        </button>
      </header>

      <div className="sellers-section">
        <div className="sellers-header">
          <h2>Gestión de Usuarios</h2>
          <button 
            className="add-seller-btn" 
            onClick={() => {
              setShowForm(true);
              setShowEditForm(false);
              setEditingSeller(null);
            }}
            disabled={isAddUserButtonDisabled()}
            title={getAddUserButtonTooltip()}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Agregar Usuario
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}

        {showForm ? (
          <SellerForm 
            onSellerAdded={handleSellerAdded} 
            onCancel={() => setShowForm(false)} 
          />
        ) : null}

        {showEditForm && editingSeller ? (
          <EditSellerForm 
            seller={editingSeller}
            onSellerUpdated={handleSellerUpdated}
            onCancel={() => {
              setShowEditForm(false);
              setEditingSeller(null);
            }}
          />
        ) : null}

        <div className="sellers-list">
          {sellers.length === 0 ? (
            <p>No hay vendedores registrados.</p>
          ) : (
            <table className="sellers-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Punto de Venta</th>
                  <th>Tipo de Usuario</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {sellers.map(seller => (
                  <tr key={seller.id}>
                    <td>{seller.full_name}</td>
                    <td>{seller.email}</td>
                    <td>{seller.pos_name || 'No asignado'}</td>
                    <td>
                      {seller.role === 'manager' 
                        ? (seller.is_general_supervisor 
                            ? 'Supervisor General' 
                            : 'Supervisor de POS')
                        : 'Vendedor'
                      }
                    </td>
                    <td>
                      <span className={`status ${seller.alive ? 'active' : 'inactive'}`}>
                        {seller.alive ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="edit-btn"
                          onClick={() => handleEditClick(seller)}
                          disabled={isUpdating}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                          Editar
                        </button>
                        <button 
                          className={`toggle-status-btn ${seller.alive ? 'deactivate' : 'activate'}`}
                          onClick={() => handleToggleStatus(seller.id, seller.alive)}
                          disabled={isUpdating}
                        >
                          {seller.alive ? 'Deshabilitar' : 'Habilitar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sellers;
